import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Play, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const DEFAULT = {
  cibil: 720, monthly_income: 75000, existing_emi: 8000, avg_bank_balance: 35000,
  employment_type: "salaried", state: "Maharashtra", city: "Mumbai",
  vehicle_make: "Maruti", vehicle_model: "Baleno", vehicle_year: 2021, vehicle_km: 45000,
  ownership_count: 1, cardr_grade: "B", vehicle_value: 700000,
  requested_loan_amount: 500000, requested_tenure_months: 60,
};

const EMP_TYPES = ["salaried", "self_employed", "business_owner", "professional", "trader", "contractor", "farmer", "transport_operator", "commission_agent", "gig_worker", "other"];

export default function RuleSimulatorTab() {
  const [form, setForm] = useState(DEFAULT);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const run = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      ["cibil", "monthly_income", "existing_emi", "avg_bank_balance", "vehicle_year", "vehicle_km", "ownership_count", "vehicle_value", "requested_loan_amount", "requested_tenure_months"].forEach(k => { payload[k] = Number(payload[k]) || 0; });
      const { data } = await api.post("/policy/simulate", payload);
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Simulation failed"); }
    finally { setBusy(false); }
  };

  const VERDICT_STYLES = {
    "Eligible": "bg-emerald-100 text-emerald-800 border-emerald-300",
    "Refer": "bg-amber-100 text-amber-800 border-amber-300",
    "Not Eligible": "bg-red-100 text-red-800 border-red-300",
  };
  const VERDICT_ICONS = { "Eligible": CheckCircle2, "Refer": AlertTriangle, "Not Eligible": XCircle };
  const VIcon = result ? VERDICT_ICONS[result.verdict] : null;

  const num = (label, key, step) => (
    <div><Label className="text-xs">{label}</Label><Input type="number" step={step || 1} value={form[key]} onChange={e => set(key, e.target.value)} data-testid={`sim-${key}`} className="mt-1 h-8" /></div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="rule-simulator-tab">
      <Card className="p-5 border-slate-200">
        <div className="text-sm font-semibold text-slate-900 mb-4">Simulate a borrower + vehicle against the live policy</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {num("CIBIL (0 = no hit)", "cibil")}
          {num("Monthly income (₹)", "monthly_income")}
          {num("Existing EMI (₹)", "existing_emi")}
          {num("Avg bank balance (₹)", "avg_bank_balance")}
          <div>
            <Label className="text-xs">Employment type</Label>
            <Select value={form.employment_type} onValueChange={v => set("employment_type", v)}>
              <SelectTrigger className="mt-1 h-8" data-testid="sim-employment"><SelectValue /></SelectTrigger>
              <SelectContent>{EMP_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">State</Label><Input value={form.state} onChange={e => set("state", e.target.value)} className="mt-1 h-8" /></div>
          {num("Vehicle year", "vehicle_year")}
          {num("Vehicle km", "vehicle_km")}
          {num("Ownership count", "ownership_count")}
          <div>
            <Label className="text-xs">CarDr grade</Label>
            <Select value={form.cardr_grade} onValueChange={v => set("cardr_grade", v)}>
              <SelectTrigger className="mt-1 h-8" data-testid="sim-grade"><SelectValue /></SelectTrigger>
              <SelectContent>{["A", "B", "C", "D", "Reject"].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {num("Vehicle value (₹)", "vehicle_value")}
          {num("Requested loan (₹)", "requested_loan_amount")}
          {num("Tenure (months)", "requested_tenure_months")}
        </div>
        <Button onClick={run} disabled={busy} className="bg-teal-600 hover:bg-teal-700 mt-4 w-full" data-testid="sim-run-btn">
          <Play size={16} className="mr-1" /> Run simulation
        </Button>
      </Card>

      <Card className="p-5 border-slate-200" data-testid="sim-result">
        {!result ? (
          <div className="text-sm text-slate-500">Run a simulation to see decision, terms, partner routing and exception levels.</div>
        ) : (
          <div className="space-y-4">
            <div className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-lg font-semibold ${VERDICT_STYLES[result.verdict]}`} data-testid="sim-verdict">
              {VIcon && <VIcon size={20} />} {result.verdict}
              <span className="ml-auto text-sm font-mono">CarFin {result.carfin_score} · Band {result.risk_band}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <KV l="Max loan" v={formatINR(result.max_loan_amount)} />
              <KV l="Max LTV" v={result.max_ltv != null ? `${Math.round(result.max_ltv * 100)}%` : "—"} />
              <KV l="Suggested ROI" v={`${result.suggested_roi}%`} />
              <KV l="ROI range" v={result.roi_range ? `${result.roi_range[0]}–${result.roi_range[1]}%` : "—"} />
              <KV l="Tenure" v={`${result.suggested_tenure} mo`} />
              <KV l="EMI" v={formatINR(result.emi)} />
              <KV l="Down payment" v={formatINR(result.required_down_payment)} />
              <KV l="FOIR" v={`${Math.round(result.foir * 1000) / 10}%`} />
              <KV l="Exception level" v={result.exception_level || "None"} />
            </div>
            <div className="flex flex-wrap gap-1.5 text-[11px]">
              <Chip on={result.own_book_eligible} label="RFC own-book" />
              <Chip on={result.co_lending_eligible} label="Co-lending" />
              <Chip on={result.sell_down_eligible} label="Sell-down" />
              <Chip on={result.gps_required} label="GPS required" warn />
              <Chip on={result.guarantor_required} label="Guarantor required" warn />
              <Chip on={result.coborrower_required} label="Co-borrower required" warn />
              <Chip on={result.manual_review} label="Manual review" warn />
            </div>
            {result.eligible_partners?.length > 0 && (
              <div className="text-sm">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Eligible lender partners</div>
                {result.eligible_partners.map(p => (
                  <div key={p.id} className="flex justify-between border border-slate-100 rounded-md p-2 mb-1">
                    <span className="font-medium">{p.name}</span>
                    <span className="font-mono text-xs">{p.split_pct}% split · {p.roi_range?.[0]}–{p.roi_range?.[1]}%</span>
                  </div>
                ))}
              </div>
            )}
            {result.rules_triggered?.length > 0 && (
              <div className="p-3 rounded-md bg-orange-50 border border-orange-200 text-orange-900 text-sm">
                <div className="font-semibold mb-1">Policy rules triggered</div>
                <ul className="list-disc pl-5 space-y-0.5">{result.rules_triggered.map(r => <li key={r} className="font-mono text-xs">{r}</li>)}</ul>
              </div>
            )}
            {result.reason_codes?.length > 0 && (
              <div className="p-3 rounded-md bg-blue-50 border border-blue-200 text-blue-900 text-sm">
                <div className="font-semibold mb-1">Reason codes</div>
                <ul className="list-disc pl-5 space-y-0.5">{result.reason_codes.map(r => <li key={r}>{r}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

function KV({ l, v }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{l}</div>
      <div className="font-mono font-semibold text-slate-900">{v}</div>
    </div>
  );
}

function Chip({ on, label, warn }) {
  if (!on) return null;
  return <span className={`px-2 py-0.5 rounded-full border ${warn ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-emerald-50 border-emerald-300 text-emerald-800"}`}>{label}</span>;
}
